import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Icon,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import { ClipConfigProps, ClipStepsControlProps } from "../../clip-menu";
import { makeStyles } from "@mui/styles";
import { useEffect, useState } from "react";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import { BoundaryItem } from "./boundary-item";
import area from "@turf/area";
import BboxSvg from "../../icons/bbox.svg?react";
import CircleSvg from "../../icons/circle.svg?react";
import PolygonSvg from "../../icons/polygon.svg?react";
import MapboxDraw, {
  DrawCreateEvent,
  DrawModeChangeEvent,
  DrawSelectionChangeEvent,
  DrawUpdateEvent,
} from "@mapbox/mapbox-gl-draw";

export const DrawComponent = (
  props: ClipConfigProps & ClipStepsControlProps
) => {
  const classes = useStyles();
  const [features, setFeatures] = useState<GeoJSON.Feature<GeoJSON.Geometry>[]>(
    props.clipConfig.geometry
      ? props.clipConfig.geometry.features.map((x) => x)
      : []
  );

  const [selectedFeature, setSelectedFeature] = useState<string | null>(null);
  const [currentMode, setCurrentMode] = useState<
    MapboxDraw.DrawMode | "draw_rectangle" | "draw_circle"
  >(MapboxDraw.constants.modes.SIMPLE_SELECT);

  const updateConfigGeometry = (
    features: GeoJSON.Feature<GeoJSON.Geometry>[]
  ) => {
    props.setClipConfig((configFeatureCollection) => {
      configFeatureCollection.geometry = {
        type: "FeatureCollection",
        features: features,
      };
      return configFeatureCollection;
    });
  };

  const handleSelectMap = (e: DrawSelectionChangeEvent) => {
    const multiSelect = e.features.length > 1;
    const first = e.features.pop();
    if (!first) {
      setSelectedFeature(null);
      return;
    }
    setSelectedFeature(first.id as string);

    // Hack to stop the user multi-selecting
    if (multiSelect) {
      //@ts-ignore
      props.draw.changeMode("direct_select", {
        featureId: first.id,
      });
    }
  };

  const handleSelectSidebar = (id: string) => {
    props.draw.changeMode("simple_select", { featureIds: [id] });
    setSelectedFeature(id);
  };

  const handleDelete = (id: string) => {
    props.draw.delete([id]);
    setFeatures((prev) => {
      const newFeatures = prev.filter((x) => x.id != id);
      updateConfigGeometry(newFeatures);
      return newFeatures;
    });
  };

  const handleRename = (id: string, name: string) => {
    setFeatures((prev) => {
      const newFeatures = prev.map((feature) => {
        if (feature.id === id) {
          if (feature.properties) {
            feature.properties.name = name;
          }
        }
        return feature;
      });
      updateConfigGeometry(newFeatures);
      return newFeatures;
    });
  };

  const handleModeChange = (e: DrawModeChangeEvent) => {
    setCurrentMode(e.mode);
  };

  const onUpdate = (e: DrawUpdateEvent) => {
    setFeatures((prevFeatures) => {
      let feat = e.features[0];

      const newFeatures = prevFeatures.map((f) => {
        if (f.id === feat.id) {
          f.geometry = feat.geometry;
          const prevName = f.properties?.name;
          f.properties = { ...feat.properties, name: prevName };
        }
        return f;
      });
      updateConfigGeometry(newFeatures);
      return newFeatures;
    });
  };

  const onCreate = (e: DrawCreateEvent) => {
    setFeatures((prev) => {
      let feat = e.features[0];
      if (!feat.properties) {
        feat.properties = {};
      }
      feat.properties.name = `Boundary ${prev.length + 1}`;
      const newFeatures = [...prev, feat];
      updateConfigGeometry(newFeatures);
      return newFeatures;
    });
  };

  useEffect(() => {
    //@ts-ignore
    props.mapRef?.current.on("draw.create", onCreate);
    //@ts-ignore
    props.mapRef?.current.on("draw.update", onUpdate);
    //@ts-ignore
    props.mapRef?.current.on("draw.selectionchange", handleSelectMap);
    //@ts-ignore
    props.mapRef?.current.on("draw.modechange", handleModeChange);
  }, []);
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={2}
      height="calc(86vh - 64px - 256px)"
    >
      <Typography variant="subtitle1" color="text.secondary">
        Use the drawing tools below to draw a boundary
      </Typography>
      <Box display="flex" flexDirection="row" gap={1}>
        <Card
          className={
            currentMode === "draw_rectangle"
              ? classes.selectedDrawType
              : classes.drawTypes
          }
          variant="outlined"
        >
          <CardActionArea
            onClick={() => {
              props.draw.changeMode("draw_rectangle");
              setCurrentMode("draw_rectangle");
            }}
          >
            <CardContent className={classes.drawTypes}>
              <Icon sx={{ textAlign: "center" }}>
                <BboxSvg fill={isDark ? "white" : "black"} />
              </Icon>
            </CardContent>
          </CardActionArea>
        </Card>
        <Card
          className={
            currentMode === "draw_polygon"
              ? classes.selectedDrawType
              : classes.drawTypes
          }
          variant="outlined"
        >
          <CardActionArea
            onClick={() => {
              props.draw.changeMode("draw_polygon");
              setCurrentMode("draw_polygon");
            }}
          >
            <CardContent className={classes.drawTypes}>
              <Icon sx={{ textAlign: "center" }}>
                <PolygonSvg fill={isDark ? "white" : "black"} />
              </Icon>
            </CardContent>
          </CardActionArea>
        </Card>
        <Card
          className={
            currentMode === "draw_circle"
              ? classes.selectedDrawType
              : classes.drawTypes
          }
          variant="outlined"
        >
          <CardActionArea
            onClick={() => {
              props.draw.changeMode("draw_circle");
              setCurrentMode("draw_circle");
            }}
          >
            <CardContent className={classes.drawTypes}>
              <Icon sx={{ textAlign: "center" }}>
                <CircleSvg fill={isDark ? "white" : "black"} />
              </Icon>
            </CardContent>
          </CardActionArea>
        </Card>
      </Box>
      <Box display="flex" flexDirection="column" gap={1} overflow="auto">
        <Box display="flex" flexDirection="column" gap={1}>
          {features.map((feature, idx) => (
            <BoundaryItem
              key={feature.id as string}
              id={feature.id as string}
              selected={feature.id == selectedFeature}
              name={feature.properties?.name ?? `Boundaries ${idx}`}
              area={area(feature)}
              onDelete={handleDelete}
              onEdit={handleRename}
              onSelect={handleSelectSidebar}
            ></BoundaryItem>
          ))}
        </Box>
      </Box>

      <Box alignSelf="flex-end" marginTop="auto" display="flex" mb={2}>
        <Button
          disabled={features.length === 0}
          onClick={() => {
            props.setClipConfig((prev) => {
              props.setActiveStep(
                Math.min(props.activeStep + 1, props.maxSteps)
              );
              return prev;
            });
          }}
        >
          Confirm
        </Button>
      </Box>
    </Box>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  drawTypes: {
    width: "40px",
    height: "40px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
  selectedDrawType: {
    width: "40px",
    height: "40px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: theme.palette.action.focus,
  },
}));
